import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Gavel, Ticket, ShieldCheck, Ghost, Clock, type LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { apiService, OnlineSummaryResponse, OnlinePlayerDTO, TodaySanctionsCount } from '@/services/api.service';
import { getRankDisplay, getRankWeight, hexToRgba } from '@/config/ranks';
import styles from './DashboardPage.module.scss';

export function DashboardPage() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const [summary, setSummary] = useState<OnlineSummaryResponse | null>(null);
  const [players, setPlayers] = useState<OnlinePlayerDTO[]>([]);
  const [todaySanctions, setTodaySanctions] = useState<TodaySanctionsCount | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // Refresh toutes les 15 secondes
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    const [summaryResult, playersResult, sanctionsResult] = await Promise.all([
      apiService.getOnlineSummary(),
      apiService.getOnlinePlayers(),
      apiService.getTodaySanctionsCount(),
    ]);

    if (summaryResult.data) setSummary(summaryResult.data);
    if (playersResult.data) setPlayers(playersResult.data.players);
    if (sanctionsResult.data) setTodaySanctions(sanctionsResult.data);

    setLoading(false);
  };

  // Tri par poids de grade décroissant (grade le plus élevé en premier), puis par nom.
  const byRankThenName = (a: OnlinePlayerDTO, b: OnlinePlayerDTO) => {
    const wa = getRankWeight(a.flags?.PRIMARY_GROUP_ID, a.flags?.IS_PREMIUM === '1');
    const wb = getRankWeight(b.flags?.PRIMARY_GROUP_ID, b.flags?.IS_PREMIUM === '1');
    if (wb !== wa) return wb - wa;
    return a.name.localeCompare(b.name);
  };

  const staffPlayers = players.filter((p) => p.isStaff).sort(byRankThenName);
  const regularPlayers = players.filter((p) => !p.isStaff).sort(byRankThenName);

  const handlePlayerClick = async (player: OnlinePlayerDTO) => {
    // Rechercher le joueur par nom pour obtenir son UUID en base
    const result = await apiService.searchPlayer(player.name);
    if (result.data?.uuid) {
      navigate(`/players/${result.data.uuid}`);
    } else {
      navigate('/players');
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Dashboard</h1>
        <p className={styles.subtitle}>
          Bienvenue, {user?.gamertag}. Voici un aperçu de l'activité du serveur.
        </p>
      </header>

      <div className={styles.statsGrid}>
        <StatsCard
          title="Joueurs en ligne"
          value={loading ? '--' : summary?.totalPlayers.toString() ?? '0'}
          icon={Users}
        />
        <StatsCard
          title="Sanctions aujourd'hui"
          value={loading ? '--' : todaySanctions?.total.toString() ?? '0'}
          icon={Gavel}
        />
        <StatsCard title="Tickets ouverts" value="--" icon={Ticket} />
        <StatsCard
          title="Staff en ligne"
          value={loading ? '--' : staffPlayers.length.toString()}
          icon={ShieldCheck}
        />
      </div>

      <div className={styles.content}>
        <OnlineBlock
          title="Staff en ligne"
          icon={ShieldCheck}
          count={staffPlayers.length}
          players={staffPlayers}
          loading={loading}
          emptyText="Aucun staff en ligne"
          onPlayerClick={handlePlayerClick}
        />
        <OnlineBlock
          title="Joueurs en ligne"
          icon={Users}
          count={regularPlayers.length}
          players={regularPlayers}
          loading={loading}
          emptyText="Aucun joueur en ligne"
          onPlayerClick={handlePlayerClick}
        />
      </div>
    </div>
  );
}

// ==================== STATS CARD ====================

interface StatsCardProps {
  title: string;
  value: string;
  icon: LucideIcon;
}

function StatsCard({ title, value, icon: Icon }: StatsCardProps) {
  return (
    <div className={styles.statsCard}>
      <div className={styles.statsCardIcon}><Icon size={24} strokeWidth={2} /></div>
      <div className={styles.statsCardContent}>
        <span className={styles.statsCardValue}>{value}</span>
        <span className={styles.statsCardTitle}>{title}</span>
      </div>
    </div>
  );
}

// ==================== ONLINE BLOCK ====================

interface OnlineBlockProps {
  title: string;
  icon: LucideIcon;
  count: number;
  players: OnlinePlayerDTO[];
  loading: boolean;
  emptyText: string;
  onPlayerClick: (player: OnlinePlayerDTO) => void;
}

function OnlineBlock({ title, icon: Icon, count, players, loading, emptyText, onPlayerClick }: OnlineBlockProps) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          <Icon size={18} strokeWidth={2} />
          <span>{title}</span>
        </h2>
        {!loading && <span className={styles.playerCount}>{count}</span>}
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <span>Chargement...</span>
        </div>
      ) : players.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}><Ghost size={40} strokeWidth={1.5} /></span>
          <p>{emptyText}</p>
        </div>
      ) : (
        <div className={styles.blockList}>
          {players.map((player) => (
            <PlayerRow key={player.uuid} player={player} onClick={() => onPlayerClick(player)} />
          ))}
        </div>
      )}
    </section>
  );
}

// ==================== PLAYER ROW ====================

function formatDuration(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  return `${minutes}m`;
}

interface PlayerRowProps {
  player: OnlinePlayerDTO;
  onClick: () => void;
}

function PlayerRow({ player, onClick }: PlayerRowProps) {
  const rank = getRankDisplay(player.flags?.PRIMARY_GROUP_ID);

  return (
    <div className={styles.playerRow} onClick={onClick} role="button" tabIndex={0}>
      <div className={styles.playerLeft}>
        <img
          src={`https://mc-heads.net/avatar/${encodeURIComponent(player.name)}/32`}
          alt=""
          className={styles.playerAvatar}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = 'https://mc-heads.net/avatar/MHF_Steve/32';
          }}
        />
        <div className={styles.playerInfo}>
          <span className={styles.playerName}>
            <span>{player.name}</span>
            <span
              className={styles.rankBadge}
              style={{ color: rank.color, backgroundColor: hexToRgba(rank.color, 0.15) }}
            >
              {rank.label}
            </span>
          </span>
          <span className={styles.playerServer}>{player.serverName || 'Inconnu'}</span>
        </div>
      </div>
      <div className={styles.playerMeta}>
        <Clock size={14} strokeWidth={2} className={styles.playerTimeIcon} />
        <span className={styles.playerTime}>{formatDuration(player.connectedSince)}</span>
      </div>
    </div>
  );
}
